import type { Vec2 } from '../terrain.js';

/**
 * The half-angle a blade of `widthM` subtends at the middle of its length — the
 * rule by which a swung blade reaches past the ends of its sweep. Every swing,
 * not only the axe's, widens by it.
 */
export function sweptBladeHalfAngleRad(widthM: number, innerM: number, reachM: number): number {
  const middleM = (innerM + reachM) / 2;
  return Math.min(Math.PI, widthM / (2 * middleM));
}

/** A curved rectangular blade: radial length × tangential arc width at its
 * midpoint. The same annular sector owns collision, coverage and rendering. */
export function axeSweepGeometry(reachM: number, headLengthM: number, headWidthM: number, sweepAngleRad: number) {
  const innerM = Math.max(0, reachM - headLengthM);
  const bladeHalfAngleRad = sweptBladeHalfAngleRad(headWidthM, innerM, reachM);
  const shaftAngleRad = Math.min(2 * Math.PI, sweepAngleRad);
  const headAngleRad = Math.min(2 * Math.PI, sweepAngleRad + 2 * bladeHalfAngleRad);
  const headAreaM2 = (reachM ** 2 - innerM ** 2) * headAngleRad / 2;
  const shaftAreaM2 = innerM ** 2 * shaftAngleRad / 2;
  return { innerM, bladeHalfAngleRad, headAreaM2, shaftAreaM2, areaM2: headAreaM2 + shaftAreaM2 };
}

/** Exact disk overlap with an annular sector, including radial end caps. */
export function diskTouchesAnnularSector(point: Vec2, radiusM: number, innerM: number, outerM: number, fromRad: number, toRad: number): boolean {
  const distance = Math.hypot(point.x, point.y);
  if (distance + radiusM < innerM || distance - radiusM > outerM) return false;
  const middle = (fromRad + toRad) / 2;
  const relativeAngle = Math.atan2(Math.sin(Math.atan2(point.y, point.x) - middle), Math.cos(Math.atan2(point.y, point.x) - middle));
  const half = (toRad - fromRad) / 2;
  if (half >= Math.PI || Math.abs(relativeAngle) <= half) return true;
  const edge = middle + Math.sign(relativeAngle) * half;
  const c = Math.cos(edge), s = Math.sin(edge);
  const projection = Math.max(innerM, Math.min(outerM, point.x * c + point.y * s));
  return Math.hypot(point.x - c * projection, point.y - s * projection) <= radiusM + 1e-9;
}
